import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import page from '../StockDispatchPage.module.css'
import styles from './Pallets.module.css'
import ov from './Overview.module.css'
import lc from './List.module.css'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { useHelpPref } from '../../lib/preferences'
import { useRefetch } from '../../lib/connection'
import { AsAt } from '../../components/AsAt'
import { PageHeader, PageBody } from '../../components/PageHeader'
import { approvalChip, type Chip } from './orders'
import { loadChip, type LoadState } from './loads'
import { TILE_TO, dueWhen, exceptionTo, loadTo, orderTo, type Overview } from './overview'

// The S&D Overview (V4 SD-01; handoff §7n.1a): a week ahead of work, read
// forward from today, with no date to set. It edits nothing: every tile and
// row is a link into the list or record it counts.

const ChipEl = ({ c }: { c: Chip }) => (
  <span className={styles.chip} style={{ background: c.bg, color: c.ink }}>
    {c.label}
  </span>
)

export function OverviewPage() {
  const { user } = useAuth()
  const [data, setData] = useState<Overview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [asAt, setAsAt] = useState<Date | null>(null)
  const [tick, setTick] = useState(0)
  const [helpOn, toggleHelp] = useHelpPref(user, 'overview')
  const seq = useRef(0)

  useEffect(() => {
    if (!user) return
    const n = ++seq.current
    apiJson<Overview>(user, '/api/stock/overview')
      .then((d) => {
        if (n !== seq.current) return
        setData(d)
        setError(null)
        setAsAt(new Date())
      })
      .catch((e) => {
        if (n === seq.current) setError('Failed to fetch the overview: ' + (e?.body?.error || e?.message || String(e)))
      })
  }, [user, tick])
  useRefetch(() => setTick((t) => t + 1))

  return (
    <div className={page.container}>
      {/* V4 titles the Overview with the module's name; Hide help sits in the body, above the tiles. */}
      <PageHeader title="Stock & Dispatch">{data && <AsAt at={asAt} />}</PageHeader>

      <PageBody>
        <div className={ov.body}>
          {error && (
            <div className={page.error}>
              <p>{error}</p>
            </div>
          )}

          <div className={ov.helpRow}>
            <button type="button" className={lc.helpPill} onClick={toggleHelp} aria-expanded={helpOn}>
              {helpOn ? 'Hide help' : 'How this overview works'}
            </button>
          </div>

          {helpOn && <OverviewHelp onClose={toggleHelp} />}

          {!data && !error && (
            <div className={styles.sub} role="status">
              Loading the overview…
            </div>
          )}

          {data && (
            <>
              <div className={ov.tiles}>
                {data.tiles.map((t) => (
                  <Link key={t.key} to={TILE_TO[t.key] || '/stock-dispatch'} className={ov.tile} data-testid={'tile-' + t.key}>
                    <span className={ov.tileK}>{t.label}</span>
                    <span className={ov.tileV}>{t.value}</span>
                    <span className={ov.tileS}>{t.sub}</span>
                  </Link>
                ))}
              </div>

              <div className={ov.cols}>
                <div className={ov.card}>
                  <div className={ov.cardHead}>Orders due</div>
                  {data.due.length === 0 && <div className={ov.empty}>No submitted orders with outstanding boxes in the next week.</div>}
                  {data.due.map((r) => {
                    const w = dueWhen(r.due, r.days)
                    return (
                      <Link key={r.id} to={orderTo(r.id)} className={`${ov.row} ${ov.due}`} data-testid="ov-due">
                        <span className={ov[w.tone]}>{w.label}</span>
                        <span className={ov.ref}>{r.reference}</span>
                        <span className={ov.who}>
                          {r.customer || '—'} <span className={ov.dim}>→ {r.destination || '—'}</span>
                        </span>
                        <span className={ov.figure} title="dispatched / ordered boxes">
                          {r.dispatched} / {r.ordered} boxes
                        </span>
                        <span>
                          <ChipEl c={approvalChip(r.approval_status)} />
                        </span>
                      </Link>
                    )
                  })}
                </div>
                <div className={ov.card}>
                  <div className={ov.cardHead}>Dispatch preparation</div>
                  {data.loads.length === 0 && <div className={ov.empty}>No loads planned in the next week.</div>}
                  {data.loads.map((l) => {
                    const w = dueWhen(l.date, l.days)
                    return (
                      <Link key={l.id} to={loadTo(l.id)} className={`${ov.row} ${ov.load}`} data-testid="ov-load">
                        <span className={ov.when}>
                          <span className={ov[w.tone]}>{w.label}</span>
                          {l.time && <span className={ov.time}>{l.time}</span>}
                        </span>
                        <span className={ov.ref}>{l.reference}</span>
                        <span className={ov.who}>
                          {l.carrier || '—'} <span className={ov.dim}>· {l.orders || '—'}</span>
                        </span>
                        <span className={ov.figure}>
                          {l.verified} / {l.pallets} pallets
                        </span>
                        <span>
                          <ChipEl c={loadChip(l.state as LoadState)} />
                        </span>
                      </Link>
                    )
                  })}
                </div>
              </div>

              <div className={ov.card}>
                <div className={ov.cardHead}>Needs attention</div>
                {data.exceptions.length === 0 && <div className={ov.empty}>Nothing needs attention.</div>}
                {data.exceptions.map((e, i) => (
                  <Link key={e.kind + e.id + i} to={exceptionTo(e)} className={ov.exc} data-testid="ov-exception">
                    <span className={ov.excRef}>{e.ref}</span>
                    <span className={ov.excWhat}>{e.what}</span>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </PageBody>
    </div>
  )
}

// V4 "How this overview works", word for word.
function OverviewHelp({ onClose }: { onClose: () => void }) {
  return (
    <div className={lc.help} data-testid="overview-help">
      <div className={lc.helpHead}>
        <span className={lc.helpTitle}>How this overview works</span>
        <span className={lc.helpLead}>A week ahead of work, not a report. Every figure is a link into the list it came from.</span>
        <button type="button" className={lc.helpX} title="Hide help" aria-label="Hide help" onClick={onClose}>
          ×
        </button>
      </div>
      <div className={lc.helpItem}>
        <span>It reads forward from today</span>
        <p>
          There is no date to set. Orders due and Dispatch prep show the week ahead with anything late at the top, so the screen answers “what needs
          doing” rather than “what happened on a chosen day”.
        </p>
      </div>
      <div className={lc.helpItem}>
        <span>Two units, never mixed</span>
        <div className={lc.defs}>
          <span>
            <strong>Stock counts</strong> = boxes
          </span>
          <span>
            <strong>Pallet counts</strong> = physical load progress
          </span>
          <span>A calculated pallet equivalent is never shown as confirmed pallets.</span>
        </div>
      </div>
      <div className={lc.helpItem}>
        <span>Every tile is a way in</span>
        <p>Clicking a tile opens the list it counts, already filtered to those records. Nothing here can be edited. The overview reports, the lists act.</p>
      </div>
      <div className={lc.helpItem}>
        <span>Exceptions are things only a person can settle</span>
        <p>
          Pallet discrepancies, orders short against a hold, a pallet verified onto a load it was not planned for. They stay listed until someone
          resolves them; the module never clears one by recalculating.
        </p>
      </div>
    </div>
  )
}
